import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const POLICIES = {
  "refund-policy": { title: "Refund Policy", default: `All sales of digital printable packs are final. Since our products are digital downloads delivered instantly, we generally do not offer refunds.\n\nHowever, if your file is faulty, corrupted, or doesn't arrive within 24 hours of a confirmed payment, please contact us. We will either fix the issue and resend, or issue a full refund.\n\nRefund requests must be made within 7 days of purchase. Refunds are processed to the original payment method within 5-7 business days.\n\nContact us on Instagram @playawayclub for any issues.` },
  "terms": { title: "Terms & Conditions", default: `By purchasing from Playaway Club, you agree to the following:\n\n1. Our printable activity packs are for personal, household use only.\n2. You may not share, redistribute, resell, or commercially use our digital files.\n3. Each purchase grants a license for one household. You may print unlimited copies for personal use within your household.\n4. We reserve the right to update these terms at any time.\n5. Prices are in Indian Rupees (INR) and include all applicable taxes.\n6. Digital products are delivered via email and secure download link after payment confirmation.\n\nContact us on Instagram @playawayclub for questions.` },
  "privacy": { title: "Privacy Policy", default: `Playaway Club respects your privacy.\n\nWhat we collect:\n- Email address (to deliver your purchase)\n- Instagram handle (optional, for customer support)\n- Order and payment details (to process and track your purchase)\n\nHow we use it:\n- To deliver your purchased digital products\n- To send order confirmations and receipts\n- To contact you if there's an issue with your order\n\nWe do NOT:\n- Share or sell your personal information to third parties\n- Send marketing emails unless you opt in\n- Store payment card details (handled securely by Razorpay)\n\nContact us on Instagram @playawayclub to request data deletion.` },
  "contact": { title: "Contact Us", default: `Get in touch with Playaway Club:\n\nInstagram: @playawayclub\nhttps://ig.me/m/playawayclub\n\nFor order issues, refunds, or general questions, please reach out via Instagram. We typically respond within 24 hours.\n\nBusiness hours: Mon-Sat, 10am-6pm IST` },
};

export default function PolicyPage() {
  const { slug } = useParams();
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api.get(`/policies/${slug}`);
        setTitle(data.title);
        setContent(data.content);
      } catch {
        const policy = POLICIES[slug];
        setTitle(policy?.title || "Page Not Found");
        setContent(policy?.default || "Page not found.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  return (
    <div className="min-h-screen bg-cream">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/">
            <Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button>
          </Link>
          <h1 className="font-heading font-bold text-lg">{title}</h1>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-8">
        {loading ? (
          <div className="animate-pulse space-y-3">
            {[1,2,3,4].map(i => <div key={i} className="h-4 bg-white rounded w-full" />)}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-6 font-body text-slate-700 text-sm leading-relaxed whitespace-pre-line">
            {content}
          </div>
        )}
      </main>
      <footer className="max-w-2xl mx-auto px-4 py-8 text-center">
        <a href="https://instagram.com/playawayclub" target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-2 text-sm text-coral font-bold">
          <Instagram className="w-4 h-4" /> @playawayclub
        </a>
      </footer>
    </div>
  );
}
